import React, { useEffect, useState } from 'react';
import './Navbar.scss';
import { useDispatch, useSelector } from "react-redux";
import { useCustomTheme } from "../../context/ThemeContext";
import {
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  Box,
  Stack,
  Collapse,
  useMediaQuery,
  Tooltip,
  Badge,
} from '@mui/material';
import HeaderYearDropdown from '../HeaderYearDropdown/HeaderYearDropdown';
import HeaderLanguageDropdown from '../HeaderYearDropdown/HeaderLanguageDropdown';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import NotificationDropdown from '../HeaderYearDropdown/NotificationDropdown';
import ProfileHeaderDropdown from '../HeaderYearDropdown/ProfileHeaderDropdown';
import AddNewDropdown from '../HeaderYearDropdown/AddNewDropdown';
import AllSchoolnameDropdown from '../HeaderYearDropdown/AllSchoolnameDropdown';
import { setCustomTheme } from '../../features/theme/themeSlice';
import { GetThemeDetails } from '../../api/Theme/ThemeColors';
import { useSnackbar } from '../MUIComponent/FeedbackComponent/SnackBar/SnackbarContext';
import SupportModalForm from '../../pages/SupportHelpTicket/SupportModalForm';
import AllChildrenDropdown from '../HeaderYearDropdown/AllChildrenDropdown';
import { Roles } from '../../constants/params/constants';
import { useParentKidAuth } from '../../hooks/parentKidUseAuth';
import Button from '../Button/Button';
import { setAcademicYear } from '../../features/academicYear/academicYearSlice';
import { useTranslation } from 'react-i18next';


const Navbar = ({ toggleSidebar }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const customColors = useSelector((state) => state.theme.customColors);
  const { isDarkMode, toggleTheme } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [rotated, setRotated] = useState(false);
  const [secondaryNavOpen, setSecondaryNavOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [openModal, setOpenModal] = useState(false);
  const headerRaw = String(customColors?.header || '').trim().toLowerCase();
  const isHeaderWhite =
    headerRaw === '#ffffff' ||
    headerRaw === 'linear-gradient(to right, #ffffff, #ffffff)';
  const useCustomHeaderColors = Boolean(customColors?.header) && !isHeaderWhite;
  const user = useSelector((state) => state.auth.user);
  const { parentkidUser } = useParentKidAuth();
  const schoolList = user?.schoolList?.length > 0 ? user.schoolList : [];

  const isRoleParent = user?.roleName === Roles[3]?.RoleName ||
    parentkidUser?.roleName === Roles[3]?.RoleName;
  const dispatch = useDispatch();
  const headerTextColor = useCustomHeaderColors
    ? theme.palette.custom.whitetext
    : theme.palette.background.text;

  const schoolProfileData = useSelector(
    (state) => state.schoolProfile.schoolProfileData
  );

  const headerBackgroundValue = useCustomHeaderColors
    ? customColors?.header
    : theme.palette.background.paper;

  const handleClick = () => {
    setRotated((prev) => !prev);
    toggleSidebar();
  };

  const handleMoreClick = () => {
    setSecondaryNavOpen((prev) => !prev);
  };

  // const currentAcademicYear = useSelector(
  //   (state) => state.academicYear.academicYear
  // );

  // useEffect(() => {
  //   if (
  // //     !schoolProfileData?.academicYearStart ||
  // //     !schoolProfileData?.academicYearEnd
  //   ) return;

  //   const startYear = new Date(schoolProfileData.academicYearStart).getFullYear();
  //   const endYear = new Date(schoolProfileData.academicYearEnd).getFullYear();

  //   const formattedAcademicYear = `${startYear}-${endYear}`;

  //   if (currentAcademicYear !== formattedAcademicYear) {
  //     dispatch(setAcademicYear(formattedAcademicYear));
  //   }
  // }, [
  //   schoolProfileData?.academicYearStart,
  //   schoolProfileData?.academicYearEnd,
  //   currentAcademicYear,
  //   dispatch,
  // ]);


useEffect(() => {
  const loadOriginalTheme = async () => {
    if (!user?.schoolID) return;

    const cacheKey = `originalTheme_${user.schoolID}`;
    const modeKey = `themeMode_${user.schoolID}`;
    let originalTheme = null;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        originalTheme = JSON.parse(cached);
      } catch (e) {
        return;
      }
    }
    if (!originalTheme) {
      try {
        const response = await GetThemeDetails(user.schoolID);
        if (response?.success && response?.data) {
          originalTheme = {
            sidebar: response.data.sidebarBackground || "",
            header: response.data.headerBackground || "",
            activeButton: response.data.activeButtonColor || "",
            activeTextColor: response.data.activeTextColor || "",
          };
          localStorage.setItem(cacheKey, JSON.stringify(originalTheme));
        }
      } catch (err) {
        originalTheme = { sidebar: "", header: "", activeButton: "", activeTextColor: "" };
      }
    }
    const savedMode = localStorage.getItem(modeKey) || 'light';
    const effectiveTheme = { ...originalTheme };

    if (savedMode === 'dark') {
      effectiveTheme.sidebar = "";
      effectiveTheme.header = "";
      effectiveTheme.activeButton = "";
      effectiveTheme.activeTextColor = "";
    }

    dispatch(setCustomTheme(effectiveTheme));
  };

  loadOriginalTheme();
}, [user?.schoolID, dispatch]);

useEffect(() => {
  if (!user?.schoolID) return;

  const cacheKey = `originalTheme_${user.schoolID}`;
  const modeKey = `themeMode_${user.schoolID}`;

  const cached = localStorage.getItem(cacheKey);
  if (!cached) return;

  try {
    const originalTheme = JSON.parse(cached);
    const effectiveTheme = { ...originalTheme };

    localStorage.setItem(modeKey, isDarkMode ? 'dark' : 'light');

    if (isDarkMode) {
      effectiveTheme.sidebar = "";
      effectiveTheme.header = "";
      effectiveTheme.activeButton = "";
      effectiveTheme.activeTextColor = "";
    }
    dispatch(setCustomTheme(effectiveTheme));
  } catch (e) {
    return "";
  }
}, [isDarkMode, user?.schoolID, dispatch]);

  const handleFullscreenToggle = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };



  const renderThemeToggle = (

    <Tooltip title={t('header.lightDarkMode')}>
      <IconButton
        className="head-icon-btn"
        onClick={toggleTheme}
        sx={{
          color: theme.palette.background.text,
          borderColor: theme.palette.custom.borderColor,
        }}
      >
        {isDarkMode ? (
          // <IconBrightnessUp stroke={2} size={16} />
          <i class="fa-sharp fa-regular fa-brightness"></i>
        ) : (
          // <IconMoon stroke={2} size={16} />
          <i class="fa-sharp fa-regular fa-moon"></i>
        )}
      </IconButton>
    </Tooltip>
  );

  return (
    <>
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1203,
        pl: { xs: 0, sm: 0, md: '0.5px' }
      }}
    >
      <AppBar
        position="sticky"
        className="custom-navbar shadow-none"
        sx={{
          borderBottom: `1px solid ${theme.palette.custom.borderColor}`,
          // borderLeft:`1px solid ${theme.palette.custom.borderColor}`,
          background: headerBackgroundValue,
          zIndex: 22222,
          ...(useCustomHeaderColors ? { color: headerTextColor } : {}),
        }}
      >
        {/* <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: useCustomHeaderColors ? headerTextColor : 'inherit',
            ...(useCustomHeaderColors
              ? {
                '& *': { color: headerTextColor },
                '& .MuiSvgIcon-root': { color: headerTextColor },
                '& .MuiTypography-root': { color: headerTextColor },
              }
              : {}),
          }}
        > */}
        <Toolbar
          sx={{
            py: { xs: '4px', sm: 0 },
            gap: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: useCustomHeaderColors ? headerTextColor : 'inherit',
            ...(useCustomHeaderColors
              ? {
                ' .MuiIconButton-root, & > .MuiSvgIcon-root, & > .MuiTypography-root': {
                  color: headerTextColor,
                },
              }
              : {
                color: 'inherit',
              }),
          }}

        >
          {/* Left side */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ display: { xs: 'none', md: 'flex' }, }}>
              <IconButton
                title={t('header.openSidebar')}
                aria-label={t('header.openSidebarMenu')}
                edge="start"
                onClick={handleClick}
                size='small'
                className='header_toggleicon'
                sx={{
                  padding: 0.5,
                  height: "20px",
                  width: "20px",
                  transition: "transform 0.5s ease",
                  // boxShadow: 'inset 1.5px 1.5px 0px rgba(255, 255, 255, .25), inset -1.5px -1.5px 0px rgba(0, 0, 0, .1)',
                  border: `1px solid ${theme.palette.custom.borderColor}`,
                  backgroundColor: theme.palette.background.default,
                  "&:hover": {
                    backgroundColor: theme.palette.background.default,
                  },
                }}
              >
                {rotated ? (

                  <i class="fa-sharp fa-regular fa-chevron-right" style={{ fontSize: "10px" }}></i>
                ) : (

                  <i class="fa-sharp fa-regular fa-chevron-left" style={{ fontSize: "10px" }}></i>
                )}
              </IconButton>
            </Box>
            <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: '0!important' }}>
              <IconButton
                title={t('header.openSidebar')}
                aria-label={t('header.openSidebarMenu')}
                edge="start"
                onClick={handleClick}
                size='small'
                // className='header_toggleicon'
                sx={{
                  padding: 0.5,
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  transition: "transform 0.5s ease",
                  border: `1px solid ${theme.palette.custom.borderColor}`,
                  backgroundColor: theme.palette.background.default,
                  "&:hover": {
                    backgroundColor: theme.palette.background.default,
                  },
                }}
              >
                {/* <IconMenu4 size={20} stroke={1.5} color={theme.palette.custom.menuText} /> */}
                <i class="fa-sharp fa-regular fa-bars"></i>
              </IconButton>
            </Box>
            <AllSchoolnameDropdown/>
          </Stack>

          {/* Right side */}
          {!isMobile ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <HeaderYearDropdown/>
              {isRoleParent && (
                <>
                  <AllChildrenDropdown />
                  <span style={{ color: '#d1d5db' }}>|</span>
                </>
              )}
              {/* <span style={{ color: '#d1d5db' }}>|</span> */}
              <LanguageSwitcher />
              {/* <span style={{ color: '#d1d5db' }}>|</span> */}
              {user?.roleName === 'Admin' && <AddNewDropdown />}
              {renderThemeToggle}
              <span style={{ color: '#d1d5db' }}>|</span>
              <NotificationDropdown />
              {/* <Tooltip title="Chat">
                <IconButton
                  className="head-icon-btn"
                  sx={{
                    color: theme.palette.background.text,
                    borderColor: theme.palette.custom.borderColor,
                  }}
                >
                  <Badge color="secondary" variant="dot">
                    <IconBrandHipchat stroke={2} size={16} />
                  </Badge>
                </IconButton>
              </Tooltip> */}


              {/* <Tooltip title="Support / Help">
                <IconButton
                  className="head-icon-btn"
                  sx={{
                    color: theme.palette.background.text,
                    borderColor: theme.palette.custom.borderColor,
                  }}
                >
                  <IconHeadset stroke={2} size={16} />
                </IconButton>
              </Tooltip> */}
              {/* <SupportModalForm /> */}
              <Tooltip title={t('header.supportHelp')}>
              <IconButton
                  onClick={() => {
    setOpenModal(true);
    setSecondaryNavOpen(false);
  }}
                sx={{
              color: theme.palette.background.text,
              borderColor: theme.palette.custom.borderColor
              , borderRadius: "15%"
            }}
              >
                <i class="fa-sharp fa-regular fa-headset fs-16"></i>
              </IconButton>
              </Tooltip>

              <Tooltip title={t('header.screenMaxMin')}>
                <IconButton
                  className="head-icon-btn"
                  onClick={handleFullscreenToggle}
                  sx={{
                    color: theme.palette.background.text,
                    borderColor: theme.palette.custom.borderColor,
                  }}
                >
                  {isFullscreen ? (
                    // <IconArrowsDiagonalMinimize2 stroke={2} size={16} />
                    <i class="fa-sharp fa-regular fa-compress fs-16"></i>

                  ) : (
                    // <IconArrowsDiagonal stroke={2} size={16} />
                    <i class="fa-sharp fa-regular fa-arrow-up-right-and-arrow-down-left-from-center fs-14"></i>
                  )}
                </IconButton>
              </Tooltip>
              <span style={{ color: '#d1d5db' }}>|</span>
              <ProfileHeaderDropdown />
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={0}>
              <ProfileHeaderDropdown />
              <Tooltip title={t('header.moreOptions')}>
                <IconButton onClick={handleMoreClick} sx={{ color: theme.palette.custom.menuText ,p:0.5}}>
                  <i class="fa-regular fa-ellipsis-vertical" style={{ fontSize: "18px" }}></i>
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      {/* Secondary nav for mobile */}
      {isMobile && (
        <Collapse in={secondaryNavOpen} timeout="auto" unmountOnExit>
          <Box
            className="secondary-navbar"
            sx={{
              background: headerBackgroundValue,
              padding: '4px',
              borderBottom: `1px solid ${theme.palette.custom.borderColor}`,
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1500,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                background: isDarkMode
                  ? customColors?.header || theme.palette.background.sidebarbg
                  : '#f9fafb',
                padding: '8px',
                borderRadius: '5px',
                justifyContent: 'center',
              }}
            >
              <HeaderYearDropdown/>
              {isRoleParent && (
                <>
                  <AllChildrenDropdown />
                  <span style={{ color: '#d1d5db' }}>|</span>
                </>
              )}
              {/* <span style={{ color: '#d1d5db' }}>|</span> */}
              <LanguageSwitcher />
              <span style={{ color: '#d1d5db' }}>|</span>
              {user?.roleName === 'Admin' && <AddNewDropdown />}
              {renderThemeToggle}
              <span style={{ color: '#d1d5db' }}>|</span>
              <NotificationDropdown />
              {/* <SupportModalForm /> */}
              <Tooltip title={t('header.supportHelp')}>
              <IconButton
                  onClick={() => {
    setOpenModal(true);
    setSecondaryNavOpen(false);
  }}
                sx={{
              color: theme.palette.background.text,
              borderColor: theme.palette.custom.borderColor
              , borderRadius: "15%"
            }}
              >
                <i class="fa-sharp fa-regular fa-headset fs-16"></i>
              </IconButton>
              </Tooltip>

            </Stack>
          </Box>
        </Collapse>
      )}
    </Box>
    <SupportModalForm
                open={openModal}
                onClose={() => setOpenModal(false)}
                showIcon={false}
                // onSuccess={() => {
                //   fetchTickets();
                //   setOpenModal(false);
                // }}
              />
    </>
  );
};

export default Navbar;
